import { makeAutoObservable } from 'mobx'
import RootStore  from 'mobx-stores/root-store'
import { Api, FetchOptions, User } from 'services'
import {
    AttributeGroupDTO,
    MediaContentDTO,
    PaginationButtonsProps,
    PaginationProps,
    SelectOption,
    SortDirection,
} from '@touche-lts/panel-ui/dist'
import { getSelectOptions } from 'utilities'
import { StoreAttributes, StoresSortBy } from 'models/stores'
import { ProductsDTO } from 'models'
import { getUniqueImages, noImgObject } from "./utils/utils"

const POST_LOGIN_NAV_KEY = 'postLoginNav'

const isRealAuthenticatedUser = (): boolean => {
  const raw = localStorage.getItem('isTempUser')
  const isTempUser = raw != null ? JSON.parse(raw) : false
  return User.isAuthenticated && !isTempUser
}

export class StoresPublic {
  private rootStore:any
  private storeApi: Api['storeApi']
  private userApi: Api['userApi']
  // OBSERVABLES
  private stores = null;
  private singleStore = null;
  private singleStoreInfo = null; 
  private searchStore: string = '';
  private storeId: number | string = 0;
  private storeInitialAttributes: StoreAttributes[] = null;
  private storeAttributes: StoreAttributes[] = null;
  private product: ProductsDTO = null;
  private attributeSelectedIds: number[] = [];
  private selectedShippingId: number = null;
  private selectedSku: string = null;
  private skuStock: string | number = null;
  private selectedImages: MediaContentDTO[] = null;
  public relatedImages: MediaContentDTO[] = null;

  public pagination: Partial<PaginationProps<any>> = {
    page: 0,
    size: 16,
    sortDirection: SortDirection.DESC,
    sortProp: 'rating',
    totalElements: 0,
    totalPages: 0
  };  
  public timeoutId: ReturnType<typeof setTimeout>
  public selectedStoreSorting: StoresSortBy = StoresSortBy.TOPRATED

  constructor (rootStore:any) {
    this.rootStore = rootStore;
    this.storeApi = this.rootStore.generalStore.api.storeApi
    this.userApi = this.rootStore.generalStore.api.userApi
    makeAutoObservable(this);
  }

    // ACTIONS
    public onPageSelect = (pageNumber: number) => {
        this.setPaginationProps({ page: pageNumber })
        this.getAllStores()
    }

    public setPaginationProps = (props: Partial<PaginationProps<any>>) => {
        this.pagination = {
        ...this.pagination,
        ...props
        }
    }

    public setStoreId = (storeId: number | string) => {
        this.storeId = storeId
    }

    public getPageQueryParams = (
        resetPageNumber: boolean,
        size?: number // Optional size parameter
    ): Partial<FetchOptions['pageQueryParams']> => {
        return {
            page: resetPageNumber ? 0 : this.pagination.page,
            size: size || this.pagination.size, // Use the passed size or fallback to the default
            sortDirection: this.pagination.sortDirection,
            sortProp: this.pagination.sortProp
        };
    }

    public setStoreSorting = (sort: StoresSortBy, singleStore?: boolean): void => {
        this.selectedStoreSorting = sort
        if(sort === StoresSortBy.TOPRATED) {
            this.setPaginationProps({ sortDirection: SortDirection.DESC })
        } else {
            this.setPaginationProps({ sortDirection: SortDirection.ASC })
        }
        if(singleStore) {
            this.getStoreById(this.storeId, this.searchStore)
            return
        }
        this.getAllStores()
    }  

    public onPageSelectSingle = (pageNumber: number) => {
        this.setPaginationProps({ page: pageNumber })
        this.getStoreById(this.storeId, this.searchStore)
    }

    public handleExpandClick = (expandId: number): void => {
        this.storeAttributes[expandId].expanded = !this.storeAttributes[expandId].expanded;
    }

    public handleCheckboxClick = (expandId: number, valueId: number): void => {
        this.storeAttributes[expandId].values[valueId].checked = !this.storeAttributes[expandId].values[valueId].checked;
        this.getStoreById(this.storeId, this.searchStore);
    }

    public removeFilterCheckBox = (expandId: number, valueId: number): void => {
        const indexSelectedCheckAttr = this.storeAttributes.findIndex(attr => attr.id === expandId)
        const indexSelectedCheckValue = this.storeAttributes[indexSelectedCheckAttr].values.findIndex(value => value.id === valueId)
        let checkedAttribute = this.storeAttributes[indexSelectedCheckAttr].values[indexSelectedCheckValue];
        checkedAttribute.checked = !checkedAttribute.checked
        this.getStoreById(this.storeId, this.searchStore);
    }

    public clearAllFilters = (): void => {
        this.storeAttributes = JSON.parse(JSON.stringify(this.storeInitialAttributes))
        this.getStoreById(this.storeId, this.searchStore);
    }

    public setRelatedImages = (images): void => {
        this.relatedImages = images;
    }

    public setAttributesPreselected = (product: ProductsDTO): void => {
        product.attGroups.forEach((attr: AttributeGroupDTO, index: number, array: AttributeGroupDTO[]) => {
            this.attributeSelectedIds.push(null);
        })
        if(!!product.shippings.length) {
            this.selectedShippingId = product.shippings[0]?.id;
        }
        this.setRelatedImages(this.product.images);
    }

    public changeSelectedAttribute = (attrValue: number, indexAttribute: number): void => {
        this.attributeSelectedIds[indexAttribute] = Number(attrValue);
        this.selectSkuAndImages();
    }

    public selectSkuAndImages = (): void => {
        for(let i = 0; i<this.productInfo?.productSkus.length; i++) {
            const ids = this.productInfo?.productSkus[i].attributes.map(e => e.id);
            if (this.attributeSelectedIds.every(id => ids.includes(id))){
                this.selectedSku = this.productInfo?.productSkus[i]?.sku;
                this.skuStock = this.productInfo?.productSkus[i]?.stock;
                // this.selectedImages = this.productInfo?.productSkus[i]?.images;
                break;
            }
        }
        this.setGalleryImages();
    }

    public setSkuByDefault = () => {
        this.selectedSku = this.productInfo?.productSkus[0]?.sku;
        this.skuStock = this.productInfo?.productSkus[0]?.stock;
    }

    public setGalleryImages(): MediaContentDTO[] {
    if (!this.product || !this.product.images) return [];
    
    // get all related products with this attributes
    const selectedAttrIds = this.attributeSelectedIds;
    const relatedSkus = this.product.productSkus.filter(sku => {
    const skuAttrIds = sku.attributes.map(attr => attr.id);

    if(this.attributeSelectedIds.every(attr => attr !== null)) {
        return selectedAttrIds.every(value => (skuAttrIds.includes(value)));
    } else {
        return selectedAttrIds.some(value => (skuAttrIds.includes(value)));
    }
    });
    const uniqueRelatedImages = getUniqueImages(relatedSkus);

        if (selectedAttrIds.length > 0 && uniqueRelatedImages.length > 0) {
            this.setRelatedImages(uniqueRelatedImages)
        } else {
            this.setRelatedImages([noImgObject]);
        }
    }

    public changeSelectedShipping = (ev): void => {
        this.selectedShippingId = ev.target.value;
    }

    // COMPUTE
    public get paginationInfo (): PaginationButtonsProps {
        return {
        elementsPerPage: this.pagination.size,
        selectedPageNumber: this.pagination.page,
        totalElements: this.pagination.totalElements,
        totalPages: this.pagination.totalPages
        }
    }


    public get pageStores () { // TODO-A To add model type
        return this.stores;
    }

    public get singlePageStore () { // TODO-A To add model type
        return this.singleStore;
    }

    public get singlePageStoreInfo () { // TODO-A To add model type
        return this.singleStoreInfo;
    }

    public get StoreSortBy (): SelectOption<StoresSortBy>[] {
        return getSelectOptions(StoresSortBy)
    }

    public get selectedStoreSortingSelected (): StoresSortBy {
        return this.selectedStoreSorting
    }

    public get storeAttributeFilters (): Partial<StoreAttributes[]> {
        return this.storeAttributes
    }

    public get storeFilterAttributes (): StoreAttributes[] {
        return this.storeAttributes ? this.storeAttributes.filter(attr => !!attr.values.filter(value => value.checked).length) : []
    }

    public get productInfo (): ProductsDTO {
        return this.product
    }

    public get selectedSKU (): string {
        return this.selectedSku;
    }

    public get selectedSkuStock (): string | number {
        return this.skuStock;
    }

    public get selectedSliderImages(): MediaContentDTO[] {
        return this.selectedImages;
    }

    // METHODS
    public getAllStores = async (options?: any, searchStore?: string): Promise<void> => {
        const initialResponse = await this.storeApi.getActiveStores(
            { pageQueryParams: this.getPageQueryParams(options?.resetPageNumber) },
            searchStore
        )
        // If no response, return early
        if (!initialResponse) return;

        // Update pagination data
        this.pagination.totalElements = initialResponse.totalElements;
        this.pagination.totalPages = initialResponse.totalPages;

        // Store the initial response data (16 items)
        this.stores = initialResponse;
        // window.localStorage.setItem('assignedStores', JSON.stringify(initialResponse.content));
        if (!window.localStorage.getItem('assignedStores')) {

            // If there are more items to fetch, make another API call to get all data
            if (initialResponse.totalElements > initialResponse.size) {
                const allStoresResponse = await this.storeApi.getActiveStores(
                    { pageQueryParams: this.getPageQueryParams(options?.resetPageNumber, initialResponse.totalElements) }, // Using totalElements as size to fetch all
                    searchStore
                );

                if (!allStoresResponse) return;

                // Store all data in localStorage (this will be all items)

                window.localStorage.setItem('assignedStores', JSON.stringify(allStoresResponse.content));

            }
        }
    };

    public getStoreById = async (
        storeId: number | string, 
        productName?: string, 
        options?: any
    ): Promise<void> => { // TODO-A To add model type
        const filterOptions = {
            productName: !!productName ? productName : '',
            attributes: this.storeAttributes ? this.getFilteredAttributes() : []
        }
        const singleStore = await this.storeApi.getSingleStore(
            storeId,
            filterOptions,
            { pageQueryParams: this.getPageQueryParams(options?.resetPageNumber) }
        )
        if(!singleStore) return;
        this.pagination.totalElements = singleStore.totalElements;
        this.pagination.totalPages = singleStore.totalPages;
        this.singleStore = singleStore;
    }

    public getSingleStoreById = async (storeId: number | string) => {
        const singleStoreInfo = await this.storeApi.getStoreById(
            storeId
        )
        if(!singleStoreInfo) return;
        this.singleStoreInfo = singleStoreInfo 
    }

    public getSingleStoreAttributes = async (storeId: number | string) => {
        const singleStoreAttributes = await this.storeApi.getSingleStoreAttributes(
            storeId
        )
        if(!singleStoreAttributes) return;
        this.storeInitialAttributes = singleStoreAttributes;
        this.storeAttributes = singleStoreAttributes;
    }

    public onSearchStore = (searchStore: string, singlerSearch?: boolean) => {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
        }
        this.timeoutId = setTimeout(
            () => {
                if(singlerSearch) {
                   this.searchStore = searchStore;
                   this.getStoreById(this.storeId, searchStore) 
                   return
                }
                this.getAllStores(null, searchStore)
            },
            800
        )
    }

    public getProductById = async (productId: number | string): Promise<ProductsDTO> => {
        this.selectedSku = null;
        this.selectedImages = null;
        const productInfo = await this.storeApi.getProductById(
            productId
        )
        if(!productInfo) return;
        this.getStoreById(productInfo.storeId)
        this.product = productInfo 
        this.attributeSelectedIds = []
        this.setAttributesPreselected(productInfo)
    }

    private getFilteredAttributes = (): StoreAttributes[] => {
        let selectedAttr = JSON.parse(JSON.stringify(this.storeAttributes));
        selectedAttr.forEach(attr => {
            attr.values = attr.values.filter(value => value.checked)
        })
        return selectedAttr.filter(attr => !!attr.values.filter(value => value.checked).length)
    }

    public addToCartProduct = async (userId: number | string, count: number, buyNow?: boolean): Promise<any> => {
        const { push } = this.rootStore.routingStore;
        if (!isRealAuthenticatedUser()) {
            const from = window.location.pathname + window.location.search
            const nav = { from, buyNow: !!buyNow }
            try {
                sessionStorage.setItem(POST_LOGIN_NAV_KEY, JSON.stringify(nav))
            } catch {
                /* ignore */
            }
            push({ pathname: '/login', state: nav })
            return
        }
        let userIdAssigned = userId ?? User.id
        if (!userIdAssigned) {
            try {
                sessionStorage.setItem(POST_LOGIN_NAV_KEY, JSON.stringify({
                    from: window.location.pathname + window.location.search,
                    buyNow: !!buyNow
                }))
            } catch {
                /* ignore */
            }
            push({ pathname: '/login', state: { from: window.location.pathname + window.location.search, buyNow: !!buyNow } })
            return
        }
        let skuId = null;
        for(let i = 0; i<this.productInfo?.productSkus.length; i++) {
            const ids = this.productInfo?.productSkus[i].attributes.map(e => e.id);
            if (this.attributeSelectedIds.every(id => ids.includes(id))){
                skuId = this.productInfo?.productSkus[i].id;
                break;
            }
        }
        const addTocartPayload = {
            product: {
                id: this.productInfo?.id
            },
            sku: {
                id: skuId
            },
            shipping: {
                id: this.selectedShippingId
            },
            count: count
        }
        const addToCart = await this.userApi.addCartByUserId(userIdAssigned, addTocartPayload)
        if(!addToCart) return
        await this.rootStore.cartCheckoutStore.getCartByUserId(userIdAssigned)
        if (buyNow) {
        this.rootStore.cartCheckoutStore.checkCartProduct(userIdAssigned, {...addToCart, checked: true})
            setTimeout(() => {
                push('/checkout');
            }, 1500);
        }
    }

}